import React, { useState, useEffect, useRef } from "react"
import SingleSelect from "@/components/input/SingleSelect"
import MultiSelect from "@/components/input/MultiSelect"

import { useInput as useUnpolyInput } from "@/unpoly/react"
import { withUnpoly } from "@/unpoly/helpers"

/**
 * Component only to be used in Rails forms
 *
 * @param multiple [Boolean, "rails"]
 */
const SelectComponent = ({
  name,
  label,
  options = [],
  placeholder = "",
  value = undefined,
  hideEmptyPlaceholder = false,
  required = false,
  multiple = false,
  unpoly = false, // New prop for Unpoly integration
  passthroughOptions = {},
}) => {
  const inputRef = useRef(null)

  const primeReactHiddenInputRef = useRef(null)

  const [selection, setSelection] = useState(() => value)

  const [selectableOptions, _] = useState(() => {
    // Filter out the blank value from the React component (even if they are selected)
    // This makes the UI look and feel better
    if (hideEmptyPlaceholder) {
      if (isEmptyPlaceholder(options[0])) {
        return options.slice(1)
      }
      return options
    }
    return options
  })

  const unpolyInput = useUnpolyInput({ ref: inputRef, enabled: unpoly })

  /* Prevent Prime React field from being submitted also.
   *
   * If we remove this field then browser-based input validation for `required`
   * does not show a pop-up any longer. Therefore we *cannot* do
   *
   *   primeReactHiddenInputRef.current.remove()
   *
   */
  useEffect(() => {
    if (!primeReactHiddenInputRef.current) return

    primeReactHiddenInputRef.current.name = ""
  })

  const handleChange = (change) => {
    setSelection(change.value)
    unpolyInput.onChange(change)

    if (unpoly && passthroughOptions.validateOnChange) {
      withUnpoly((up) => {
        up.validate(inputRef.current.closest("form"))
      })
    }
  }

  return (
    <>
      {multiple ? (
        <div>
          <MultiSelect
            /* This does not actually render a hidden <select>, but a form-input instead */
            name={name}
            label={label}
            onChange={handleChange}
            placeholder={placeholder}
            value={selection}
            options={options}
            onShow={() => unpolyInput.pauseEvents()}
            onHide={() => unpolyInput.playEvents()}
            pt={{ input: { ref: primeReactHiddenInputRef } }}
            required={required}
            {...passthroughOptions}
          />
          {/* Rails quirk with multiselect to overcome HTML form submits not submitting empty selects */}
          <input hidden name={name} value="" readOnly />
          {/* Hidden select element for multi-select */}
          <select ref={inputRef} name={name} value={selection} multiple hidden readOnly>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <div>
          <SingleSelect
            name={name}
            label={label}
            onChange={handleChange}
            placeholder={placeholder}
            value={selection}
            options={selectableOptions}
            pt={{ select: { ref: primeReactHiddenInputRef } }}
            required={required}
            {...passthroughOptions}
          />
          <select ref={inputRef} name={name} value={selection} hidden readOnly>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      )}
    </>
  )
}

const isEmptyPlaceholder = (option) => option && option.label === "" && option.value === ""

export default SelectComponent
